import type { FeatureGateSeed } from './feature-gate-registry';

/**
 * The People group's gates, spread into FEATURE_GATE_SEEDS in feature-gate-registry.ts at their
 * place in its order. electorate_crm is the electorate view
 * (docs/plans/2026-10-29-feat-electorate-crm-plan.md, U2); it changes the People page, so it adds
 * no sidebar item. explorer_buildings is the Explorer's buildings layer
 * (docs/plans/2026-10-30-feat-explorer-buildings-plan.md, D5); it adds no sidebar item either, nor
 * does explorer_analysis, the Explorer's saved analysis views
 * (docs/plans/2026-10-30-feat-explorer-agentic-analysis-plan.md, U-B1), or explorer_address_panel,
 * the G-NAF address suggestions, matching and map links on People and the Explorer.
 * people_search_v2 is the indexed People search
 * (docs/plans/2026-10-30-perf-people-search-instant-plan.md, D10); it adds no sidebar item.
 */
export const PEOPLE_FEATURE_GATE_SEEDS = {
  people: {
    label: 'People',
    shortLabel: 'People',
    description: 'People and organisation records for correspondence context and CRM workflows.',
    group: 'people_crm',
    navItemIds: ['crm'],
  },
  explorer: {
    label: 'Explorer',
    shortLabel: 'Explorer',
    description:
      'Map-based explorer for constituents and correspondence with saved regions and segment building.',
    group: 'people_crm',
    navItemIds: ['explorer'],
  },
  electorate_crm: {
    label: 'Electorate view',
    shortLabel: 'Electorate',
    description:
      'People as a list and a map of the same view, with area and engagement filters, address groups, and walk, call and task lists made from a saved view. Not a sidebar item - off, People shows the list exactly as before.',
    group: 'people_crm',
  },
  explorer_buildings: {
    label: 'Explorer buildings',
    shortLabel: 'Buildings',
    description:
      'Open-data building footprints on the Explorer map at street zoom, raised to their height where one is recorded, with a click that rings the building address points. Not a sidebar item - off, the Explorer shows no Buildings layer.',
    group: 'people_crm',
  },
  explorer_analysis: {
    label: 'Explorer analysis views',
    shortLabel: 'Analysis views',
    description:
      'Saved map analyses on the Explorer: CRM coverage per elector, census shares and issue rates by area, built by staff or Pulse AI from a fixed catalogue of measures, with small areas hidden. Not a sidebar item - off, nothing saves, shares or runs, and Pulse AI cannot build one.',
    group: 'people_crm',
  },
  explorer_address_panel: {
    label: 'Address matching',
    shortLabel: 'Addresses',
    description:
      "Official G-NAF address suggestions as staff type a person's address, each address matched to its exact site, the people recorded at an address on the Explorer, and a person's addresses on the map. Not a sidebar item - off, addresses are typed and saved exactly as before and nothing is matched.",
    group: 'people_crm',
  },
  people_search_v2: {
    label: 'People search',
    shortLabel: 'Search',
    description:
      'Indexed People search: typo, nickname, address, suburb, postcode and tag matches, best match first, the first page before the count. Not a sidebar item - off, People search works exactly as before.',
    group: 'people_crm',
  },
} satisfies Record<string, FeatureGateSeed>;
